import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Header from '@components/Header/Header'
import PhoneInput from '@components/PhoneInput/PhoneInput'
import NumericKeyboard from '@components/NumericKeyboard/NumericKeyboard'
import PrimaryButton from '@components/PrimaryButton/PrimaryButton'
import TabSelector, { Tab } from '@components/TabSelector/TabSelector'
import PackageOption, { Bonus } from '@components/PackageOption/PackageOption'
import { useNavigate } from 'react-router'
import {
  Container,
  ContentContainer,
  PhoneInputWrapper,
  KeyboardSection,
  PackageSection,
  PackageLabel,
  TabWrapper,
  PackageList,
  ButtonWrapper
} from './ESIMData.styles'

type PackageTab = 'ALL' | 'DATA_ONLY' | 'SOCIAL'

type Package = {
  id: string
  iconUrl?: string
  title: string
  idrPrice: number
  jidrPrice: number
  quotaAmount: number
  quotaUnit: string
  quotaDescription: string
  validityAmount: number
  validityUnit: string
  validityDescription: string
  extras?: Bonus[]
  category: PackageTab
}

const ESIMData = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [step, setStep] = useState<'input' | 'package'>('input')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [selectedTab, setSelectedTab] = useState<PackageTab>('ALL')
  const [selectedPackage, setSelectedPackage] = useState<string | null>(null)

  const tabs: Tab<PackageTab>[] = [
    { value: 'ALL', label: t('esimData.all') },
    { value: 'DATA_ONLY', label: t('esimData.dataOnly') },
    { value: 'SOCIAL', label: t('esimData.social') },
  ]

  const packages: Package[] = [
    {
      id: '1',
      iconUrl: 'https://s3.ap-southeast-3.amazonaws.com/assets.jagad.to/ppob/packages/MainInternetIcon.svg',
      title: 'Surprise Deal FUP 2GB 1 Day',
      idrPrice: 25000,
      jidrPrice: 24500,
      quotaAmount: 2,
      quotaUnit: 'GB',
      quotaDescription: 'Bandwidth',
      validityAmount: 1,
      validityUnit: 'Day',
      validityDescription: 'Active period',
      extras: [
        { bonusAmount: 1.7, bonusUnit: 'GB', bonusDescription: 'Night Quota' },
        { bonusAmount: 300, bonusUnit: 'MB', bonusDescription: 'TikTok' },
      ],
      category: 'ALL'
    },
    {
      id: '2',
      iconUrl: 'https://s3.ap-southeast-3.amazonaws.com/assets.jagad.to/ppob/packages/MainInternetIcon.svg',
      title: 'Weekend Special FUP 5GB 3 Days',
      idrPrice: 50000,
      jidrPrice: 49500,
      quotaAmount: 5,
      quotaUnit: 'GB',
      quotaDescription: 'Bandwidth',
      validityAmount: 3,
      validityUnit: 'Days',
      validityDescription: 'Active period',
      extras: [
        { bonusAmount: 4, bonusUnit: 'GB', bonusDescription: 'Night Quota' },
        { bonusAmount: 1, bonusUnit: 'GB', bonusDescription: 'YouTube' },
      ],
      category: 'ALL'
    },
    {
      id: '3',
      iconUrl: 'https://s3.ap-southeast-3.amazonaws.com/assets.jagad.to/ppob/packages/YoutubeIcon.svg',
      title: 'Youtube Streaming 24 Hours',
      idrPrice: 10000,
      jidrPrice: 9500,
      quotaAmount: 10,
      quotaUnit: 'GB',
      quotaDescription: 'Bandwidth',
      validityAmount: 5,
      validityUnit: 'Days',
      validityDescription: 'Active period',
      extras: [
        { bonusAmount: 10, bonusUnit: 'GB', bonusDescription: 'Youtube Quota' },
      ],
      category: 'SOCIAL'
    },
  ]

  const filteredPackages = selectedTab === 'ALL'
    ? packages
    : packages.filter(pkg => pkg.category === selectedTab)

  const handleKeyboardChange = (value: string | 'delete') => {
    if (value === 'delete') {
      setPhoneNumber(prev => prev.slice(0, -1))
    } else {
      setPhoneNumber(prev => prev + value)
    }
  }

  const handleContinue = () => {
    if (step === 'input') {
      setStep('package')
    } else {
      // Navigate to payment confirm page
      navigate('/payment-confirm', {
        state: {
          phoneNumber,
          packageId: selectedPackage,
          serviceType: 'esim-data'
        }
      })
    }
  }

  const handleBack = () => {
    if (step === 'package') {
      setStep('input')
    } else {
      navigate(-1)
    }
  }

  return (
    <Container>
      <Header
        title={t('esimData.header')}
        showBackButton={true}
        onBackClick={handleBack}
      />
      <ContentContainer>
        <PhoneInputWrapper>
          <PhoneInput
            value={phoneNumber}
            readOnly={step === 'input'}
            iconType="data"
          />
        </PhoneInputWrapper>

        {step === 'input' ? (
          <KeyboardSection>
            <NumericKeyboard onChange={handleKeyboardChange} />
            <PrimaryButton
              onClick={handleContinue}
              disabled={!phoneNumber}
            >
              {t('esimData.continue')}
            </PrimaryButton>
          </KeyboardSection>
        ) : (
          <>
            <PackageSection>
              <PackageLabel>{t('esimData.choosePackage')}</PackageLabel>
              <TabWrapper>
                <TabSelector
                  tabs={tabs}
                  activeTab={selectedTab}
                  onTabChange={setSelectedTab}
                />
              </TabWrapper>
              <PackageList>
                {filteredPackages.map((pkg) => (
                  <PackageOption
                    key={pkg.id}
                    iconUrl={pkg.iconUrl}
                    title={pkg.title}
                    idrPrice={pkg.idrPrice}
                    jidrPrice={pkg.jidrPrice}
                    quotaAmount={pkg.quotaAmount}
                    quotaUnit={pkg.quotaUnit}
                    quotaDescription={pkg.quotaDescription}
                    validityAmount={pkg.validityAmount}
                    validityUnit={pkg.validityUnit}
                    validityDescription={pkg.validityDescription}
                    extras={pkg.extras}
                    selected={selectedPackage === pkg.id}
                    onClick={() => setSelectedPackage(pkg.id)}
                  />
                ))}
              </PackageList>
            </PackageSection>
            <ButtonWrapper>
              <PrimaryButton
                onClick={handleContinue}
                disabled={!selectedPackage}
              >
                {t('esimData.continue')}
              </PrimaryButton>
            </ButtonWrapper>
          </>
        )}
      </ContentContainer>
    </Container>
  )
}

export default ESIMData
